import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Tag, Typography, Card, message, Input, Space, Modal, Descriptions } from 'antd';
import { ReloadOutlined, MailOutlined, CheckCircleOutlined, CloseCircleOutlined, LinkOutlined, EyeOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    
    h2 {
      font-size: 20px !important;
    }
    
    button {
      width: 100%;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  .ant-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
`;

const StyledTableCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 0;
  }
  
  @media (max-width: 768px) {
    .ant-card-body {
      padding: 8px !important;
      overflow-x: auto;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    .ant-table {
      min-width: 1000px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 8px 4px !important;
      font-size: 12px;
    }
    
    .ant-table-pagination {
      margin: 16px 8px !important;
      flex-wrap: wrap;
    }
    
    .ant-pagination {
      flex-wrap: wrap;
      justify-content: center;
    }
    
    .ant-pagination-options {
      margin-top: 8px;
    }
  }
`;

const EmailLogs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: Number(searchParams.get('pageId')) || 1,
    pageSize: Number(searchParams.get('perPage')) || 50,
    total: 0,
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '50');

  useEffect(() => {
    fetchEmailLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, search]);

  const fetchEmailLogs = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_EMAIL_LOGS, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.data?.itemsList || data.itemsList || (Array.isArray(data) ? data : []);
        setLogs(items);
        const paginator = data.data?.paginator || data.paginator || data.pagination;
        setPagination(prev => ({
          ...prev,
          current: paginator?.currentPage || pageId,
          pageSize: paginator?.perPage || perPage,
          total: paginator?.totalCount || paginator?.total || items.length,
        }));
      } else {
        message.error('Failed to fetch email logs');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch email logs');
      console.error('Error fetching email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    if (search) {
      newParams.set('search', search);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const handleViewAnalytics = async (messageId: string) => {
    try {
      setSelectedMessageId(messageId);
      setAnalyticsModalVisible(true);
      setLoadingAnalytics(true);
      
      const endpoint = API_ENDPOINTS.ADMIN_GET_EMAIL_ANALYTICS_BY_MESSAGE_ID.replace('{messageId}', messageId);
      const response = await apiClient.get(endpoint);
      
      if (response.data?.status === 'success' && response.data?.data) {
        setAnalyticsData(response.data.data);
      } else {
        message.error('Failed to fetch email analytics');
        setAnalyticsModalVisible(false);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch email analytics');
      setAnalyticsModalVisible(false);
      console.error('Error fetching email analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Check if mobile viewport
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: isMobile ? 180 : undefined,
      ellipsis: true,
    },
    {
      title: 'Delivery Status',
      dataIndex: 'deliveryStatus',
      key: 'deliveryStatus',
      width: isMobile ? 120 : undefined,
      render: (status: string) => {
        const statusColors: Record<string, string> = {
          delivered: 'green',
          failed: 'red',
          pending: 'orange',
          bounced: 'red',
        };
        return (
          <Tag color={statusColors[status] || 'default'}>
            {status || 'unknown'}
          </Tag>
        );
      },
    },
    {
      title: 'Opened',
      dataIndex: 'isOpen',
      key: 'isOpen',
      width: isMobile ? 80 : undefined,
      render: (isOpen: boolean) => (
        <Space>
          {isOpen ? (
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#8c8c8c' }} />
          )}
          {!isMobile && <span>{isOpen ? 'Yes' : 'No'}</span>}
        </Space>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      width: isMobile ? 70 : undefined,
      render: (views: number) => views || 0,
    },
    {
      title: 'Clicked',
      dataIndex: 'isClick',
      key: 'isClick',
      width: isMobile ? 80 : undefined,
      render: (isClick: boolean) => (
        <Space>
          {isClick ? (
            <LinkOutlined style={{ color: '#1890ff' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#8c8c8c' }} />
          )}
          {!isMobile && <span>{isClick ? 'Yes' : 'No'}</span>}
        </Space>
      ),
    },
    {
      title: 'Clicks',
      dataIndex: 'clicks',
      key: 'clicks',
      width: isMobile ? 70 : undefined,
      render: (clicks: number) => clicks || 0,
    },
    {
      title: 'Message ID',
      dataIndex: 'messageId',
      key: 'messageId',
      width: isMobile ? 150 : undefined,
      ellipsis: true,
      render: (messageId: string) => messageId ? (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewAnalytics(messageId)}
          style={{ 
            padding: 0,
            fontFamily: 'monospace', 
            fontSize: isMobile ? '10px' : '12px',
            color: COLORS.accent
          }}
        >
          {messageId.substring(0, isMobile ? 12 : 20)}...
        </Button>
      ) : '-',
    },
    {
      title: 'Sent At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: isMobile ? 140 : undefined,
      ellipsis: true,
      render: (date: string) => {
        if (!date) return '-';
        const sentDate = new Date(date);
        return (
          <span style={{ fontSize: isMobile ? '10px' : '12px' }}>
            {isMobile 
              ? sentDate.toLocaleDateString() 
              : sentDate.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: isMobile ? 140 : undefined,
      ellipsis: true,
      render: (date: string) => {
        if (!date) return '-';
        const updatedDate = new Date(date);
        return (
          <span style={{ fontSize: isMobile ? '10px' : '12px' }}>
            {isMobile 
              ? updatedDate.toLocaleDateString() 
              : updatedDate.toLocaleString()}
          </span>
        );
      },
    },
  ], [isMobile]);

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Email Logs</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchEmailLogs}
          loading={loading}
        >
          Refresh
        </Button>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong style={{ color: COLORS.textSecondary, display: 'block', marginBottom: 8 }}>
              Search
            </Text>
            <Input
              placeholder="Search by email or message ID"
              prefix={<MailOutlined />}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              onPressEnter={(e) => handleSearch((e.target as HTMLInputElement).value)}
              style={{ maxWidth: 400 }}
              allowClear
            />
          </div>
        </Space>
      </FiltersContainer>

      <StyledTableCard>
        <TableWrapper>
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="_id"
            loading={loading}
            scroll={isMobile ? { x: 1200 } : undefined}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: !isMobile,
              showTotal: (total) => (
                <span style={{ 
                  color: COLORS.textSecondary,
                  fontSize: isMobile ? '12px' : '14px'
                }}>
                  Total {total} email logs
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
              size: isMobile ? 'small' : 'default',
              showQuickJumper: !isMobile,
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size={isMobile ? 'small' : 'middle'}
          />
        </TableWrapper>
      </StyledTableCard>

      <Modal
        title="Email Analytics Details"
        open={analyticsModalVisible}
        onCancel={() => {
          setAnalyticsModalVisible(false);
          setSelectedMessageId(null);
          setAnalyticsData(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setAnalyticsModalVisible(false);
            setSelectedMessageId(null);
            setAnalyticsData(null);
          }}>
            Close
          </Button>
        ]}
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        {loadingAnalytics ? (
          <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
            Loading analytics...
          </div>
        ) : analyticsData ? (
          <Descriptions
            bordered
            column={1}
            labelStyle={{ 
              background: COLORS.bgPrimary, 
              color: COLORS.textPrimary,
              fontWeight: 'bold',
              width: '200px'
            }}
            contentStyle={{ 
              background: COLORS.bgSecondary, 
              color: COLORS.textPrimary 
            }}
          >
            <Descriptions.Item label="Message ID">
              <span style={{ fontFamily: 'monospace' }}>{selectedMessageId}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {analyticsData.data?.email || analyticsData.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Delivery Status">
              <Tag color={
                analyticsData.data?.deliveryStatus === 'delivered' ? 'green' :
                analyticsData.data?.deliveryStatus === 'failed' ? 'red' :
                analyticsData.data?.deliveryStatus === 'pending' ? 'orange' : 'default'
              }>
                {analyticsData.data?.deliveryStatus || analyticsData.deliveryStatus || '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Opened">
              <Space>
                {analyticsData.data?.isOpen || analyticsData.isOpen ? (
                  <><CheckCircleOutlined style={{ color: '#52c41a' }} /> Yes</>
                ) : (
                  <><CloseCircleOutlined style={{ color: '#8c8c8c' }} /> No</>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Views">
              {analyticsData.data?.views || analyticsData.views || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Clicked">
              <Space>
                {analyticsData.data?.isClick || analyticsData.isClick ? (
                  <><LinkOutlined style={{ color: '#1890ff' }} /> Yes</>
                ) : (
                  <><CloseCircleOutlined style={{ color: '#8c8c8c' }} /> No</>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Clicks">
              {analyticsData.data?.clicks || analyticsData.clicks || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Company ID">
              {analyticsData.data?.companyId || analyticsData.companyId || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {analyticsData.data?.createdAt || analyticsData.createdAt 
                ? new Date(analyticsData.data?.createdAt || analyticsData.createdAt).toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {analyticsData.data?.updatedAt || analyticsData.updatedAt 
                ? new Date(analyticsData.data?.updatedAt || analyticsData.updatedAt).toLocaleString()
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
            No analytics data available
          </div>
        )}
      </Modal>
    </PageContainer>
  );
};

export default EmailLogs;

